import React, { useCallback, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ui_issueTableHideDoneToggle_HideDoneItemsToggle$key } from '@atlassian/jira-relay/src/__generated__/ui_issueTableHideDoneToggle_HideDoneItemsToggle.graphql';
import { useUpdateHideDoneMutation } from '../services/update-hide-done-toggle/index.tsx';
import messages from './messages.tsx';
import { HideDoneOnboarding } from './onboarding/index.tsx';

export type HideDoneToggleProps = {
	viewResult: ui_issueTableHideDoneToggle_HideDoneItemsToggle$key;
	onChangeHideDone: (isHideDoneEnabled: boolean) => void;
	onResetSelectedIssues?: () => void;
};

export const HideDoneItemsToggle = ({
	viewResult,
	onChangeHideDone,
	onResetSelectedIssues,
}: HideDoneToggleProps) => {
	const viewResultData = useFragment<ui_issueTableHideDoneToggle_HideDoneItemsToggle$key>(
		graphql`
			fragment ui_issueTableHideDoneToggle_HideDoneItemsToggle on JiraIssueSearchViewMetadata
			@argumentDefinitions(staticViewInput: { type: "JiraIssueSearchStaticViewInput" }) {
				__id
				id
				viewId
				... on JiraIssueSearchView {
					viewConfigSettings(staticViewInput: $staticViewInput) {
						hideDone
					}
				}
				... on JiraSpreadsheetView {
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
					) {
						hideDone
					}
				}
			}
		`,
		viewResult,
	);

	const { viewConfigSettings, viewSettings, id, viewId, __id: issueSearchViewId } = viewResultData;
	const hideDoneToggleViewSettings = fg('refactor_nin_to_jira_view_schema')
		? viewSettings
		: viewConfigSettings;
	const { hideDone } = hideDoneToggleViewSettings ?? {};

	const [hideDoneIsChecked, setHideDoneIsChecked] = useState(hideDone);

	const toggleIssueHideDone = useUpdateHideDoneMutation();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onChange = useCallback(() => {
		if (id) {
			fireUIAnalytics(createAnalyticsEvent({}), 'toggle clicked', 'hideDoneItems', {
				hideDone: !hideDone,
			});
			setHideDoneIsChecked(!hideDone);
			// toggle mutation here
			toggleIssueHideDone({
				issueSearchViewResultAri: id,
				isHideDoneEnabled: !hideDone,
				issueSearchViewId,
				onChangeHideDone,
			});
			// reset the selected issue for the bulk operations when chaging the toggle
			onResetSelectedIssues && onResetSelectedIssues();
		}
	}, [
		createAnalyticsEvent,
		hideDone,
		id,
		issueSearchViewId,
		onChangeHideDone,
		onResetSelectedIssues,
		toggleIssueHideDone,
	]);

	const toggleId = `${viewId}_hide_done_toggle`;
	const { formatMessage } = useIntl();

	return (
		<Stack>
			<Inline spread="space-between" alignBlock="center">
				<label htmlFor={toggleId}>
					{formatMessage(
						fg('jira-issue-terminology-refresh-m3')
							? messages.showHideDoneItems
							: messages.showHideDoneItemsOldTerminology,
					)}
				</label>
				<Toggle
					size="large"
					id={toggleId}
					onChange={onChange}
					isChecked={Boolean(hideDoneIsChecked)}
				/>
			</Inline>
			<HideDoneOnboarding />
		</Stack>
	);
};
